/**
 * useFulfillmentData Hook
 * 
 * Clean hook for fulfillments list data management.
 * Handles fetching, filtering, pagination - components just consume.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks/redux';
import type { FulfillmentQueryParams } from '@/app/types/fulfillment';
import {
  fetchFulfillments,
  fetchStatusCounts,
  setFilters,
  setPage,
  setPageSize,
  clearFilters,
  selectFulfillments,
  selectFulfillmentsTotalCount,
  selectFulfillmentsCurrentPage,
  selectFulfillmentsPageSize,
  selectFulfillmentsFilters,
  selectFulfillmentsIsLoading,
  selectFulfillmentsError,
  selectFulfillmentsTotalPages,
  selectFulfillmentsStatusCounts,
  PAGE_SIZE_OPTIONS,
  type PageSizeOption,
} from '@/app/store/fulfillmentsSlice';

export function useFulfillmentData() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const lastPathnameRef = useRef<string>('');
  
  // Select all state
  const fulfillments = useAppSelector(selectFulfillments);
  const totalCount = useAppSelector(selectFulfillmentsTotalCount);
  const currentPage = useAppSelector(selectFulfillmentsCurrentPage);
  const pageSize = useAppSelector(selectFulfillmentsPageSize);
  const filters = useAppSelector(selectFulfillmentsFilters);
  const isLoading = useAppSelector(selectFulfillmentsIsLoading);
  const error = useAppSelector(selectFulfillmentsError);
  const totalPages = useAppSelector(selectFulfillmentsTotalPages);
  const statusCounts = useAppSelector(selectFulfillmentsStatusCounts);

  // Initial fetch on mount
  useEffect(() => {
    if (fulfillments.length === 0 && !isLoading && !error) {
      dispatch(fetchFulfillments());
      dispatch(fetchStatusCounts());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when returning to list page route
  useEffect(() => {
    const isReturningToList = location.pathname === '/fulfillments' && 
                              lastPathnameRef.current !== '/fulfillments' &&
                              lastPathnameRef.current !== '';
    
    if (isReturningToList && !isLoading && !error) {
      dispatch(fetchFulfillments());
      dispatch(fetchStatusCounts());
    }
    
    lastPathnameRef.current = location.pathname;
  }, [location.pathname, isLoading, error, dispatch]);

  // Focus/visibility handling - refetch when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isLoading && !error) {
        dispatch(fetchFulfillments());
        dispatch(fetchStatusCounts());
      }
    };

    const handleFocus = () => {
      if (!isLoading && !error) {
        dispatch(fetchFulfillments());
        dispatch(fetchStatusCounts());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isLoading, error, dispatch]);

  // Apply filters and fetch
  const applyFilters = useCallback((newFilters: Partial<FulfillmentQueryParams>) => {
    dispatch(setFilters(newFilters));
    dispatch(fetchFulfillments({ ...filters, ...newFilters, offset: 0 }));
  }, [dispatch, filters]);

  // Change page
  const goToPage = useCallback((page: number) => {
    dispatch(setPage(page));
    dispatch(fetchFulfillments({ ...filters, offset: (page - 1) * pageSize }));
  }, [dispatch, filters, pageSize]);

  // Refresh current view
  const refresh = useCallback(() => {
    dispatch(fetchFulfillments());
    dispatch(fetchStatusCounts());
  }, [dispatch]);

  // Clear all filters
  const resetFilters = useCallback(() => {
    dispatch(clearFilters());
    dispatch(fetchFulfillments());
  }, [dispatch]);

  // Change page size
  const changePageSize = useCallback((size: number) => {
    if (PAGE_SIZE_OPTIONS.includes(size as PageSizeOption)) {
      dispatch(setPageSize(size as PageSizeOption));
      dispatch(fetchFulfillments({ ...filters, limit: size, offset: 0 }));
    }
  }, [dispatch, filters]);

  return {
    // Data
    fulfillments,
    totalCount,
    statusCounts,
    
    // Pagination
    currentPage,
    pageSize,
    totalPages,
    
    // Filters
    filters,
    
    // Status
    isLoading,
    error,
    
    // Actions
    applyFilters,
    goToPage,
    changePageSize,
    refresh,
    resetFilters,
    
    // Constants
    pageSizeOptions: PAGE_SIZE_OPTIONS,
  };
}

